import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Fold } from "./Fold";
import { hasCompleteCheck, searchOfferCatalog, type OfferCatalog } from "../lib/offerCatalog";
import { shortDay } from "../lib/offersPage";
import { OfferText } from "./OfferText";

function day(value: string | null): string {
  const label = shortDay(value);
  return label && value ? `${label}, ${value.slice(0, 4)}` : "Unknown";
}

export function OfferCatalogSearch({ finished, query, cardId = null, excludeKeys = [], enrolled }: {
  finished: number;
  query?: string;
  cardId?: number | null;
  excludeKeys?: string[];
  enrolled?: boolean;
}) {
  const controlled = query !== undefined;
  const [open, setOpen] = useState(false);
  const [merchant, setMerchant] = useState("");
  const [request, setRequest] = useState<{ merchant: string } | null>(null);
  const [result, setResult] = useState<{
    request: { merchant: string }; finished: number;
    data?: OfferCatalog; error?: string;
  } | null>(null);

  useEffect(() => {
    if (query === undefined) return;
    const term = query.trim();
    const timer = setTimeout(() => setRequest(term ? { merchant: term } : null), 200);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (!request) return;
    let active = true;
    searchOfferCatalog(request.merchant).then(
      data => { if (active) setResult({ request, finished, data }); },
      (error: unknown) => {
        if (active) setResult({ request, finished, error:
          error instanceof Error ? error.message : "Could not search captured offers" });
      },
    );
    return () => { active = false; };
  }, [request, finished]);

  const current = result?.request === request && result?.finished === finished && (!controlled || request?.merchant === query.trim());
  const loading = Boolean(request && !current);
  const rawData = current ? result?.data : undefined;
  const excluded = new Set(excludeKeys);
  const data = rawData ? { ...rawData, offers: rawData.offers.filter(offer =>
    (!cardId || offer.card_id === cardId) && !excluded.has(offer.capture_key) && (enrolled === undefined || Boolean(offer.enrolled) === enrolled)),
    cards: rawData.cards.filter(card => !cardId || card.card_id === cardId),
  } : undefined;
  const error = current ? result?.error : undefined;
  const complete = data?.cards.filter(hasCompleteCheck).length ?? 0;

  if (controlled && !query.trim()) return null;

  return (
    <section aria-label="Merchant deal search">
      {!controlled && <Fold open={open} onClick={() => setOpen(!open)} label="Find a merchant deal" />}
      {(controlled || open) && (
        <div className="mt-2 rounded-xl border border-neutral-800 bg-neutral-900/30 p-4">
          <p className="text-xs text-neutral-400">Search eligible and enrolled offers saved from your cards.</p>
          {!controlled && <form aria-label="Search captured offers" className="mt-3 flex items-end gap-2" onSubmit={e => {
            e.preventDefault();
            const term = merchant.trim();
            if (term) setRequest({ merchant: term });
          }}>
            <label className="min-w-0 grow text-xs text-neutral-400">
              Merchant
              <input value={merchant} onChange={e => setMerchant(e.target.value)}
                placeholder="Merchant name, e.g. UPS" maxLength={120} required
                className="mt-1 block min-h-10 w-full rounded-lg border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm text-neutral-100" />
            </label>
            <button type="submit" disabled={!merchant.trim()} className="btn btn-primary min-h-10">Search</button>
          </form>}
          <div aria-live="polite" aria-busy={loading} className="mt-3 text-sm">
            {loading && <p className="text-neutral-400">Searching…</p>}
            {error && <p role="alert" className="text-red-300">{error}</p>}
            {data && (
              <div className="border-t border-neutral-800 pt-3">
                {data.offers.length === 0 ? (
                  <div>
                    <p className="font-semibold">{controlled ? "No additional saved offers" : "No saved offers"} for “{request?.merchant}”</p>
                    <p className="mt-1 text-xs text-neutral-400">This search covers captured offers. Cards without complete reads may still have a deal.</p>
                  </div>
                ) : (
                  <>
                    <p className="text-xs text-neutral-400">{data.offers.length} saved {data.offers.length === 1 ? "offer" : "offers"} for “{request?.merchant}”</p>
                    <p className="mt-1 text-xs text-neutral-500">Enrollment status is as of the last check. Confirm the offer and its terms with the issuer.</p>
                    <ul className="mt-2 divide-y divide-neutral-800">
                      {data.offers.map(offer => (
                        <li key={offer.capture_key} className="space-y-1.5 py-3">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <strong>{offer.merchant}</strong>
                            <span className="rounded-full border border-neutral-700 px-2 py-0.5 text-[11px] text-neutral-300">{offer.enrolled ? "Enrolled" : "Not enrolled"}</span>
                          </div>
                          <p className="text-xs text-neutral-400"><Link to={`/cards?card=${offer.card_id}&cardTab=offers`} className="text-accent underline">{offer.card_name}</Link> · {offer.issuer}</p>
                          {offer.title && <p className="text-sm text-neutral-300">{offer.title}</p>}
                          <OfferText offer={offer} />
                          <p className="text-xs text-neutral-500">{offer.expires_on ? `Expires ${day(offer.expires_on)}` : "Expiry unknown"} · Status last checked {day(offer.last_seen_on)}</p>
                          {offer.last_read_on && !hasCompleteCheck(offer) && <p className="text-xs text-amber-300">Latest card check {day(offer.last_read_on)} was partial.</p>}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                <details className="mt-4 text-xs text-neutral-400">
                  <summary className="cursor-pointer">Capture coverage · {complete} of {data.cards.length} cards have complete reads</summary>
                  <ul className="mt-2 space-y-2">
                    {data.cards.map(card => (
                      <li key={card.card_id}>
                        <span className="text-neutral-300">{card.card_name} · {card.issuer}</span>
                        <span className="mt-0.5 block">{!card.last_read_on ? "Not captured yet" : hasCompleteCheck(card)
                          ? `Complete check ${day(card.last_read_on)}`
                          : `Partial check ${day(card.last_read_on)}${card.last_complete_on ? ` · last complete ${day(card.last_complete_on)}` : " · no complete check"}`}</span>
                      </li>
                    ))}
                  </ul>
                </details>
                <p className="mt-3 text-xs text-neutral-500">These are saved observations. <Link to="/refresh" className="text-accent underline">Refresh</Link> checks supported cards again.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
